import React, { useMemo, useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { HexColorPicker } from 'react-colorful';
import { debounce } from 'lodash';
import styles from './ColorPicker.module.css';
import ColorUtils, { COLORS, DARK_COLORS } from '../../util/color';

interface ColorSliderProps {
  open: boolean;
  color: string;
  onChange: (color: string) => void;
  positionAbove?: boolean;
}

const ColorSlider: React.FC<ColorSliderProps> = ({
  open,
  onChange,
  color,
  positionAbove,
}) => {
  const onChangeDebounced = useMemo(() => debounce(onChange, 200), [onChange]);

  if (!open) return null;

  return (
    <div className={styles.customColorPickerContainer}>
      <div
        className={classnames(styles.customColorPicker, {
          [styles.customColorPickerAbove]: positionAbove,
        })}
      >
        <HexColorPicker color={color} onChange={onChangeDebounced} />
      </div>
    </div>
  );
};

interface ColorPickerSwatchProps {
  color: string;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  className?: string;
  border?: boolean;
}

const ColorPickerSwatch: React.FC<ColorPickerSwatchProps> = ({
  color,
  onClick,
  className,
  border,
}) => {
  return (
    <div className={classnames(styles.colorSwatchContainer, className)}>
      <div
        className={classnames(styles.colorSwatch, {
          [styles.colorSwatchBorder]: border,
        })}
        style={{ backgroundColor: color }}
        onClick={onClick}
      />
    </div>
  );
};

interface CustomColorPickerSwatchProps {
  color: string;
  onClick: React.MouseEventHandler<HTMLDivElement>;
}

const CustomColorPickerSwatch: React.FC<CustomColorPickerSwatchProps> = ({
  color,
  onClick,
}) => {
  return (
    <div className={styles.customColorSwatch}>
      <ColorPickerSwatch
        border={color === '#ffffff'}
        color={color}
        onClick={onClick}
      />
    </div>
  );
};

interface ColorPickerProps {
  currentColor: string;
  handleColorChange: (color: string, focusEditor?: boolean) => void;
  handleClearColor: () => void;
  handleCloseMenu: () => void;
  showClearButton?: boolean;
  showCustomColorInput?: boolean;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  currentColor,
  handleColorChange,
  handleClearColor,
  handleCloseMenu,
  showClearButton,
  showCustomColorInput,
}) => {
  const colors = ColorUtils.darkMode() ? DARK_COLORS : COLORS;
  const [positionAbove, setPositionAbove] = useState(false);
  const [inputValue, setInputValue] = useState(currentColor);
  const [customColorPickerOpen, setCustomColorPickerOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const showColorPickerFooter = showCustomColorInput || showClearButton;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    setInputValue(color);
    if (ColorUtils.isValidHexColor(color)) {
      handleColorChange(color, false);
    }
  };

  const toggleCustomColorPicker = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setCustomColorPickerOpen((open) => !open);
  };

  const stopPropagation = useMemo(() => {
    const targetClasses = [
      styles.customColorPicker,
      styles.customColorHexInput,
      styles.colorSwatches,
      styles.colorControls,
      'react-colorful',
    ];

    const handler = (e: React.MouseEvent<HTMLDivElement>) => {
      if (
        targetClasses.some(
          (className) =>
            e.target instanceof HTMLElement &&
            e.target.className.includes(className)
        )
      ) {
        e.stopPropagation();
      }
    };

    return {
      onMouseDown: handler,
      onClick: handler,
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const checkPosition = () => {
      const container = containerRef.current;
      if (!container) return;

      // Find the toolbar element that contains this color picker
      const toolbar = container.closest(
        '[data-element-toolbar-path-depth]'
      ) as HTMLElement;
      if (!toolbar) return;

      const toolbarRect = toolbar.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Estimate the height of the color picker (approximately 200px)
      const colorPickerHeight = 220;
      const spaceBelow = viewportHeight - toolbarRect.bottom;
      const spaceAbove = toolbarRect.top;

      // If there's not enough space below but there is above, position above
      if (spaceBelow < colorPickerHeight && spaceAbove > colorPickerHeight) {
        setPositionAbove(true);
      } else {
        setPositionAbove(false);
      }
    };

    // Debounce the position checking to improve performance
    const debouncedCheckPosition = debounce(checkPosition, 100);

    // Check position immediately and on scroll/resize
    setTimeout(() => checkPosition(), 20);
    window.addEventListener('scroll', debouncedCheckPosition, true);
    window.addEventListener('resize', debouncedCheckPosition);

    return () => {
      window.removeEventListener('scroll', debouncedCheckPosition, true);
      window.removeEventListener('resize', debouncedCheckPosition);
      debouncedCheckPosition.cancel();
    };
  }, []);

  return (
    <div ref={containerRef} className={styles.colorPicker} {...stopPropagation}>
      <div className={styles.colorSwatches}>
        {colors.map((color) => (
          <ColorPickerSwatch
            key={color}
            color={color}
            onClick={() => {
              handleColorChange(color, true);
              handleCloseMenu();
            }}
          />
        ))}
      </div>
      {showColorPickerFooter && (
        <React.Fragment>
          <div className={styles.colorControls}>
            {showCustomColorInput && (
              <div className={styles.customColor}>
                <CustomColorPickerSwatch
                  color={currentColor}
                  onClick={toggleCustomColorPicker}
                />
                <div className={styles.customColorHex}>
                  <input
                    type="text"
                    value={inputValue.toUpperCase()}
                    onChange={handleInputChange}
                    className={styles.customColorHexInput}
                  />
                </div>
              </div>
            )}
            {showClearButton && (
              <div>
                <button
                  type="button"
                  className={styles.clearColorButton}
                  onClick={() => {
                    handleClearColor();
                    handleCloseMenu();
                  }}
                >
                  <i className="fa-regular fa-droplet-slash" /> None
                </button>
              </div>
            )}
          </div>
          {showCustomColorInput && (
            <ColorSlider
              open={customColorPickerOpen}
              onChange={(color) => {
                handleColorChange(color, false);
                setInputValue(color);
              }}
              color={currentColor}
              positionAbove={positionAbove}
            />
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default ColorPicker;
